import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import EyeIcon from '../../assets/image/eye-icon.svg'
import LeftAngle from '../../assets/image/left-angle.svg'
import RightAngle from '../../assets/image/right-angle.svg'
import { Link, useSearchParams } from "react-router-dom";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import Toastify, { notifyError } from "../../utils/tostfy";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListPage } from "../../hooks/useListQueryPersistence";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/my-requests";

const roles = ["Accountant", "Fuel Operator"];
const VALID_ROLES = new Set(roles);
const tabs = ["All", "Pending", "Approved", "Rejected"];

const STATUS_BY_TAB = {
  Pending: 0,
  Approved: 1,
  Rejected: 2,
};

const REQUEST_STATUS_LABEL = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${pad2(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const mapRequestStatus = (status) =>
  REQUEST_STATUS_LABEL[Number(status)] ?? "-";

const mapAccountantRow = (item) => ({
  id: item?._id || "",
  requestId: item?.request_ref_id || "-",
  date: formatDate(item?.submitted_at || item?.createdAt),
  type: item?.request_type || "-",
  submittedBy: item?.submitted_by?.full_name || "-",
  location: item?.mipl_location?.name || "-",
  status: mapRequestStatus(item?.status),
  statusCode: item?.status,
});

const mapFuelOperatorRow = (item) => ({
  id: item?._id || "",
  requestId: item?.request_ref_id || "-",
  date: formatDate(item?.createdAt || item?.updatedAt),
  type: "Fuel Form",
  submittedBy: item?.fuel_operator?.full_name || "-",
  location: item?.fuel_pump?.fuel_pump_name || item?.fuel_pump?.address || "-",
  status: mapRequestStatus(item?.request_status),
  statusCode: item?.request_status,
});

const MyRequests = () => {
  const [searchParams] = useSearchParams();
  const [activeRole, setActiveRole] = useState(() => {
    const r = searchParams.get("role") || "Accountant";
    return VALID_ROLES.has(r) ? r : "Accountant";
  });
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "All");
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const [itemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const applyRestoredState = useCallback((saved) => {
    if (saved.role && VALID_ROLES.has(saved.role)) setActiveRole(saved.role);
    if (saved.tab) setActiveTab(saved.tab);
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
  }, []);

  const { skipPageResetRef, detailListQuery } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      role: activeRole,
      tab: activeTab !== "All" ? activeTab : undefined,
    },
    applyRestoredState
  );

  useLayoutEffect(() => {
    if (skipPageResetRef.current) return;
    setCurrentPage(1);
  }, [activeRole, activeTab, skipPageResetRef]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending":
        return "text-yellow-600";
      case "Approved":
        return "text-green-600";
      case "Rejected":
        return "text-red-600";
      default:
        return "";
    }
  };

  const fetchRequests = async () => {
    setListLoading(true);
    try {
      const statusParam =
        activeTab !== "All" && STATUS_BY_TAB[activeTab] != null
          ? `&status=${STATUS_BY_TAB[activeTab]}`
          : "";
      const pagination = `page=${currentPage}&limit=${itemsPerPage}`;
      const isFuelOperator = activeRole === "Fuel Operator";
      const apiPath = isFuelOperator
        ? `${endpoint.listFuelFormChangeRequests}?${pagination}${statusParam}`
        : `${endpoint.listChangeRequestsForAdmin}?${pagination}${statusParam}`;

      const response = await handleApiCall("get", apiPath, {});

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const raw = isFuelOperator
          ? d?.items
          : d?.change_request_data ?? d?.changeRequestData;
        const list = Array.isArray(raw) ? raw : [];
        const mapped = list.map(
          isFuelOperator ? mapFuelOperatorRow : mapAccountantRow
        );
        setRows(mapped);
        setTotalRecords(d?.total ?? mapped.length);
        setTotalPages(Math.max(1, d?.total_pages ?? 1));
      } else {
        setRows([]);
        setTotalRecords(0);
        setTotalPages(1);
        // const errMsg =
        //   response?.response?.data?.errorMessage ||
        //   response?.response?.data?.message ||
        //   response?.data?.message ||
        //   "Failed to load requests";
        // notifyError(errMsg);
        notifyError(getApiErrorMessage(response, 'Failed to load requests'));
      }
    } catch {
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
      notifyError("Failed to load requests");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [activeRole, activeTab, currentPage, itemsPerPage]);

  const displayFrom =
    totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <div className="mt-2">
      <Toastify/>
      {/* Role Tabs */}
      <div className="flex md:gap-3 gap-1.5 mb-3 font-UbuntuRegular">
        {roles.map((role) => (
          <button
            key={role}
            onClick={() => setActiveRole(role)}
            className={`md:!px-6 !px-4 md:min-h-12 min-h-9 max-[767px]:text-xs !rounded-full border transition-all hover:!bg-danger hover:!border-danger hover:!text-white ${
              activeRole === role
                ? "!bg-danger text-white"
                : "!text-black !border-gray-500"
            }`}
          >
            {role}
          </button>
        ))}
      </div>

      {/* Status Tabs */}
      <div className="flex md:gap-3 gap-1.5 mb-3 font-UbuntuRegular">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`md:!px-6 !px-4 md:py-2 py-1 md:min-w-28 max-[767px]:text-xs !rounded-full border !border-primary transition-all hover:!bg-primary hover:!text-white ${
              activeTab === tab
                ? "!bg-primary text-white"
                : "!text-black !border-gray-500"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="">
        <div className="common-table-wrapper m-0 shadow-sm">
          <div className="table-responsive">
            <table className="w-full text-sm">
              
              <thead className="bg-gray-700 text-white">
                <tr>
                  <th className="p-3 text-left">S.No.</th>
                  <th className="p-3 text-left">Request ID</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Submitted By</th>
                  <th className="p-3 text-left">MIPL Location</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>

              <tbody className="max-[767px]:[&_td]:!py-2">
                {listLoading ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-500">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  rows.map((item, index) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td>
                        {String(displayFrom + index).padStart(2, "0")}
                      </td>
                      <td>{item.requestId}</td>
                      <td>{item.date}</td>
                      <td>{item.type}</td>
                      <td>{item.submittedBy}</td>
                      <td>{item.location}</td>
                      <td className={`font-medium ${getStatusStyle(item.status)}`}>
                        {item.status}
                      </td>
                      <td className="text-center">
                        <Link
                          to={`/my-requests/view-request-retails?request_id=${encodeURIComponent(item.id)}&role=${encodeURIComponent(activeRole)}${detailListQuery}`}
                        >
                          <img src={EyeIcon} className='h-7 min-w-[22px] inline-block' alt="View"/>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-end items-center gap-4 md:!p-4 !pt-3 text-sm">
          <span className="text-xs md:text-sm text-gray-500 md:ml-auto mr-auto">
            {totalRecords === 0
              ? "0 records found"
              : `Showing ${displayFrom}-${displayTo} of ${totalRecords} records`}
          </span>

          <div className="flex items-center gap-2 [&>input]:w-10">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || listLoading}
              className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
            >
              <img src={LeftAngle} className="invert" alt="" />
            </button>

            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={listLoading}
            />

            <span className="text-gray-500">of {totalPages}</span>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage >= totalPages || listLoading}
              className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
            >
              <img src={RightAngle} className="invert" alt="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyRequests;
